<template>
  <BCard class="twilio-comparison mb-4">
    <h2 class="h4">Compare with Twilio</h2>
    <p>Twilio outcomes alongside our recorded proposals. This comparison does not change SMS sending.</p>
    <form class="twilio-dates" @submit.prevent="applyFilters">
      <label>From ({{ result?.timezone || 'Europe/Stockholm' }})<input v-model="from" required type="date" class="form-control" /></label>
      <label>To ({{ result?.timezone || 'Europe/Stockholm' }})<input v-model="to" required type="date" class="form-control" :min="from" /></label>
      <button class="btn btn-green" :disabled="loading">Refresh comparison</button>
    </form>
    <p v-if="loading" role="status">Loading Twilio comparison…</p>
    <p v-if="error" role="alert" class="mt-3">{{ error }}</p>
    <template v-if="result">
      <p v-if="!connected" role="status">Live updates are disconnected. Displayed data may be outdated; refresh to check again.</p>
      <div class="twilio-sync mt-3">
        <strong>{{ result.weeklyEnabled ? 'Weekly sync configured' : 'Automatic sync not enabled' }}</strong>
        <span v-if="result.weeklyEnabled"> · {{ result.schedule }}</span>
        <p class="mb-1">Last successful API sync ({{ result.timezone }}): {{ result.lastSuccessAt || 'None' }}</p>
        <p v-if="result.stale" class="mb-1">
          A recent successful API sync is missing. Check coverage before drawing conclusions.
        </p>
        <p v-if="result.lastRun" class="mb-1">
          Latest run: {{ result.lastRun.source === 'csv' ? 'Manual CSV import' : 'API sync' }} ·
          {{ result.lastRun.status }} · {{ result.lastRun.finishedAt || 'In progress' }}
        </p>
        <p v-if="result.lastRun?.error" role="alert">{{ result.lastRun.error }}</p>
        <p v-if="result.lastRun" class="mb-0">{{ result.lastRun.coverageNote }}</p>
        <p v-else class="mb-0">No Twilio data has been imported yet.</p>
      </div>
      <p class="mt-3">
        {{ result.internalCoverage.observations }} internal observations in this period;
        {{ result.internalCoverage.withoutTwilioMatch }} without a matching Twilio log.
      </p>
      <p class="mt-3">
        Delivered does not prove an SMS was necessary. A Twilio block does not prove fraud. Historical messages without
        an internal observation cannot be used to measure our detection rate.
      </p>
      <div class="table-responsive">
        <table class="table">
          <caption>
            Comparison of
            {{
              result.messages.count
            }}
            outgoing SMS ·
            {{
              result.from
            }}–{{
              result.to
            }}
            (UTC)
          </caption>
          <thead>
            <tr>
              <th>Twilio outcome</th>
              <th>Would allow</th>
              <th>Would pause</th>
              <th>Would block</th>
              <th>No comparison</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(label, outcome) in outcomes" :key="outcome">
              <th scope="row">{{ label }}</th>
              <td v-for="decision in decisions" :key="decision">{{ count(outcome, decision) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <details>
        <summary>Inspect individual SMS outcomes</summary>
        <EasyDataTable
          ref="dataTable"
          v-model:server-options="serverOptions"
          :server-items-length="result.messages.count"
          :items="result.messages.data"
          :headers="headers"
          :loading="loading"
          :rows-per-page="protectionPageSize"
          :rows-items="[protectionPageSize]"
          table-class-name="vue3-easy-data-table table-shadow no-overflow"
          alternating
          hide-footer
          @update:server-options="updateServerOptions"
        >
          <template #empty-message>No imported outgoing SMS in this period.</template>
          <template #item-messageAt="row">
            {{ row.messageAt }}<br /><small>{{ row.sid }}</small>
          </template>
          <template #item-outcome="row">
            {{ outcomes[row.outcome as TwilioOutcome] }}<br />{{ row.status }} {{ row.errorCode || '' }}
          </template>
          <template #item-internalDecision="row">
            <strong>{{ proposalLabel(row) }}</strong>
            <template v-if="row.internalObservation">
              <ul class="mb-1">
                <li v-for="reason in row.internalObservation.reasons" :key="reason">
                  {{ protectionReasonLabel(reason, row.internalObservation.evidence) }}
                </li>
              </ul>
              <p v-if="!row.internalObservation.reasons.length" class="mb-1">
                {{
                  row.internalDecision === 'allow'
                    ? 'No rule triggered.'
                    : 'No reason was recorded — investigate this observation.'
                }}
              </p>
              <small>Rule version: {{ row.internalObservation.ruleVersion }} · Observation #{{
                row.internalObservation.id
              }}</small>
              <details>
                <summary>Evidence recorded at the time</summary>
                <pre>{{ JSON.stringify(row.internalObservation.evidence, null, 2) }}</pre>
              </details>
            </template>
            <p v-else class="mb-0">No reliable recorded reason is available for comparison.</p>
          </template>
        </EasyDataTable>
        <TablePagination
          v-model:server-options-ref="serverOptions"
          v-model:data-table-ref="dataTable"
          :inert="loading"
          :server-items-length="result.messages.count"
        />
      </details>
    </template>
  </BCard>
</template>

<script lang="ts">
import type { TwilioComparisonResponse } from '@/types/Api/SmsProtection'
import type { ProtectionDecision, TwilioOutcome, TwilioMessage } from '@/types/Models/SmsProtection'
import type { Ref } from 'vue'
import { isAxiosError } from 'axios'
import { BCard } from 'bootstrap-vue-next'
import { defineComponent, ref } from 'vue'
import { useMessagingProtectionUpdates } from '@/composables/useMessagingProtectionUpdates'
import { protectionPageSize } from '@/const/MessagingProtection'
import { protectionReasonLabel } from '@/utils/smsProtection'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import SmsProtectionProxy from '@/proxies/SmsProtectionProxy'

export default defineComponent({
  name: 'TwilioProtectionComparison',
  components: { BCard, EasyDataTable, TablePagination },
  setup() {
    const dataTable: Ref<InstanceType<typeof EasyDataTable> | null> = ref(null)
    const serverOptions: Ref<ServerOptions> = ref({ page: 1, rowsPerPage: protectionPageSize })
    const loading: Ref<boolean> = ref(false)
    const error: Ref<string> = ref('')

    const result: Ref<TwilioComparisonResponse | null> = ref(null)
    const from: Ref<string> = ref('')
    const to: Ref<string> = ref('')
    let appliedDates = { from: '', to: '' }
    const outcomes: Record<TwilioOutcome, string> = {
      pumping_blocked: 'Pumping blocked (30450)',
      delivered: 'Delivered',
      other_failure: 'Other failure',
      pending: 'Unconfirmed / pending',
    }
    const decisions: (ProtectionDecision | 'unknown')[] = ['allow', 'would_pause', 'would_block', 'unknown']
    const proposals: Record<ProtectionDecision | 'unknown', string> = {
      allow: 'Would allow',
      would_pause: 'Would pause',
      would_block: 'Would block',
      unknown: 'No internal observation',
    }
    const headers: Header[] = [
      { text: 'Time / Message SID', value: 'messageAt' },
      { text: 'Twilio result', value: 'outcome' },
      { text: 'Our proposal and recorded reasons', value: 'internalDecision' },
    ]

    const proposalLabel = (row: TwilioMessage): string => {
      if (row.observationCount && row.observationCount > 1) return 'Ambiguous observation match'
      return proposals[row.internalDecision]
    }

    const count = (outcome: TwilioOutcome, decision: ProtectionDecision | 'unknown'): number =>
      result.value?.summary.find((row) => row.outcome === outcome && row.internalDecision === decision)?.total ?? 0

    const load = async (): Promise<void> => {
      if (loading.value) return
      loading.value = true
      error.value = ''
      try {
        result.value = await new SmsProtectionProxy().twilio(appliedDates.from, appliedDates.to, serverOptions.value.page)
        if (!from.value) from.value = result.value.from
        if (!to.value) to.value = result.value.to
      } catch (cause) {
        result.value = null
        error.value =
          isAxiosError(cause) && [404, 503].includes(cause.response?.status || 0)
            ? 'Twilio sync is not connected to this environment yet.'
            : 'Could not load Twilio comparison. Check access and try again.'
      } finally {
        loading.value = false
      }
    }

    const applyFilters = (): void => {
      appliedDates = { from: from.value, to: to.value }
      serverOptions.value = { ...serverOptions.value, page: 1 }
      void refresh()
    }

    const updateServerOptions = (options: ServerOptions): void => {
      serverOptions.value = options
      void refresh()
    }

    const { connected, refresh } = useMessagingProtectionUpdates('twilio', load)

    return {
      result,
      loading,
      error,
      from,
      to,
      outcomes,
      decisions,
      headers,
      dataTable,
      serverOptions,
      protectionPageSize,
      count,
      load: refresh,
      connected,
      applyFilters,
      updateServerOptions,
      proposalLabel,
      protectionReasonLabel,
    }
  },
})
</script>
